/**
 * Die Bruecke zwischen Regelkern und Darstellung.
 *
 * Die Ansicht liest den Zustand und gleicht die Szene an. Sie schreibt nie zurueck
 * und trifft keine Regelentscheidung - deshalb bleibt der Kern ohne Oberflaeche
 * pruefbar und die Ansicht ohne Regelwissen austauschbar.
 */
import * as THREE from 'three'
import type { HotelId } from '../core/data/hotels'
import { HOTELS } from '../core/data/hotels'
import { FELDER, autoPunkt } from '../core/data/spielplan'
import type { Zustand } from '../core/typen'
import { baueBrett } from './brett'
import { baueFreizeitpark, baueGebaeude } from './gebaeude'
import { baueAuto, baueEingang, stelleAuto } from './figuren'
import { EBENE } from './ebenen'
import { deckflaeche } from './formen'

export interface Ansicht {
  readonly wurzel: THREE.Group
  /** Gleicht die Szene an den Zustand an. */
  zeige(z: Zustand): void
  /** Hebt ein Feld hervor, etwa das Ziel des laufenden Zuges. */
  markiereFeld(index: number | null): void
  /** Zeigt waehlbare Felder an, etwa fuer den Bau eines Eingangs. */
  zeigeAuswahl(felder: readonly number[]): void
  /**
   * Welches waehlbare Feld ist gemeint? Zuerst der genaue Treffer, sonst das
   * naechstgelegene hervorgehobene Feld im Umkreis - Felder sind klein, und ein
   * Klick knapp daneben soll nicht folgenlos bleiben.
   */
  feldUnter(kamera: THREE.Camera, ndcX: number, ndcY: number): number | null
  /** Treibt laufende Bewegungen weiter. `dt` in Sekunden. */
  aktualisiere(dt: number): void
  /** Steht alles still? Solange nicht, wartet der naechste Bot. */
  ruht(): boolean
}

export function baueAnsicht(): Ansicht {
  const wurzel = new THREE.Group()
  wurzel.add(baueBrett())

  const bauten = new THREE.Group()
  const eingaenge = new THREE.Group()
  const autos = new THREE.Group()
  wurzel.add(bauten, eingaenge, autos)

  const gezeigteBauten = new Set<string>()
  const gezeigteEingaenge = new Set<string>()
  const autoObjekte = new Map<number, THREE.Object3D>()

  const marke = new THREE.Mesh(
    ringGeometrie(),
    new THREE.MeshBasicMaterial({
      color: '#ffd75e',
      transparent: true,
      opacity: 0.85,
      depthWrite: false,
    }),
  )
  marke.visible = false
  wurzel.add(marke)

  // Unsichtbare Trefferflaechen ueber jedem Feld, damit man Felder anklicken kann.
  const treffer = new THREE.Group()
  treffer.visible = false
  const trefferMaterial = new THREE.MeshBasicMaterial({ visible: false })
  for (const feld of FELDER) {
    const m = new THREE.Mesh(deckflaeche(FELDER[feld.index]!.ecken, EBENE.symbol + 0.4), trefferMaterial)
    m.userData.feld = feld.index
    treffer.add(m)
  }
  wurzel.add(treffer)

  // Sichtbare Hervorhebung waehlbarer Felder.
  const auswahl = new THREE.Group()
  wurzel.add(auswahl)
  const auswahlMaterial = new THREE.MeshBasicMaterial({
    color: '#7fe0a0',
    transparent: true,
    opacity: 0.42,
    depthWrite: false,
  })
  const auswahlFlaechen = FELDER.map((feld) => {
    const m = new THREE.Mesh(deckflaeche(FELDER[feld.index]!.ecken, EBENE.symbol + 0.3), auswahlMaterial)
    m.visible = false
    auswahl.add(m)
    return m
  })

  const strahl = new THREE.Raycaster()

  /** Eine laufende Fahrt eines Autos ueber mehrere Felder. */
  interface Fahrt {
    readonly objekt: THREE.Object3D
    readonly punkte: readonly { x: number; z: number }[]
    readonly richtungen: readonly number[]
    readonly dauer: number
    zeit: number
  }
  let fahrt: Fahrt | null = null
  let letzterWeg = ''

  /** Ein Gebaeude, das gerade aus dem Boden waechst. */
  interface Wuchs {
    readonly objekt: THREE.Object3D
    zeit: number
  }
  const wuechse: Wuchs[] = []
  const WUCHSDAUER = 0.42

  let erstesBild = true

  function zeige(z: Zustand): void {
    // Gebaeude und Freizeitparks.
    const soll = new Set<string>()
    for (const besitz of Object.values(z.besitz)) {
      if (!besitz) continue
      for (let i = 0; i < besitz.gebaeude; i++) soll.add(`${besitz.hotel}#${i}`)
      if (besitz.freizeitpark) soll.add(`${besitz.hotel}#park`)
    }
    for (const schluessel of soll) {
      if (gezeigteBauten.has(schluessel)) continue
      const [hotel, teil] = schluessel.split('#') as [HotelId, string]
      const neu = teil === 'park' ? baueFreizeitpark(hotel) : baueGebaeude(hotel, Number(teil))
      bauten.add(neu)
      gezeigteBauten.add(schluessel)
      if (erstesBild) continue
      // Frisch gebaut: sichtbar aus dem Boden wachsen lassen.
      neu.scale.y = 0.04
      wuechse.push({ objekt: neu, zeit: 0 })
    }
    for (const schluessel of [...gezeigteBauten]) {
      if (soll.has(schluessel)) continue
      const [hotel, teil] = schluessel.split('#') as [HotelId, string]
      const name = teil === 'park' ? `${hotel}-park` : `${hotel}-${teil}`
      const objekt = bauten.getObjectByName(name)
      if (objekt) bauten.remove(objekt)
      gezeigteBauten.delete(schluessel)
    }

    // Eingaenge.
    const sollEingang = new Set(z.eingaenge.map((e) => `${e.hotel}@${e.feld}`))
    for (const e of z.eingaenge) {
      const schluessel = `${e.hotel}@${e.feld}`
      if (gezeigteEingaenge.has(schluessel)) continue
      eingaenge.add(baueEingang(e.hotel, e.feld))
      gezeigteEingaenge.add(schluessel)
    }
    for (const schluessel of [...gezeigteEingaenge]) {
      if (sollEingang.has(schluessel)) continue
      const [hotel, feld] = schluessel.split('@')
      const objekt = eingaenge.getObjectByName(`eingang-${hotel}-${feld}`)
      if (objekt) eingaenge.remove(objekt)
      gezeigteEingaenge.delete(schluessel)
    }

    // Autos.
    const proFeld = new Map<number, number[]>()
    for (const s of z.spieler) {
      if (s.ausgeschieden) continue
      const liste = proFeld.get(s.feld) ?? []
      liste.push(s.id)
      proFeld.set(s.feld, liste)
    }
    for (const s of z.spieler) {
      let auto = autoObjekte.get(s.id)
      if (s.ausgeschieden) {
        if (auto) {
          autos.remove(auto)
          autoObjekte.delete(s.id)
        }
        continue
      }
      if (!auto) {
        auto = baueAuto(s.farbe)
        autos.add(auto)
        autoObjekte.set(s.id, auto)
      }
      const liste = proFeld.get(s.feld)!
      if (fahrt?.objekt === auto) continue
      stelleAuto(auto, s.feld, liste.indexOf(s.id), liste.length)
    }

    starteFahrt(z)
    erstesBild = false
  }

  /**
   * Startet die Fahrtanimation, wenn der Zustand einen neuen Weg meldet. Der Weg
   * kommt aus dem Regelkern - die Ansicht rechnet nicht selbst nach, wohin ein Auto
   * gehoert.
   */
  function starteFahrt(z: Zustand): void {
    const weg = z.anzeige.weg
    const kennung = `${z.amZug}:${weg.join(',')}`
    if (weg.length === 0 || kennung === letzterWeg || erstesBild) {
      letzterWeg = kennung
      return
    }
    letzterWeg = kennung
    const auto = autoObjekte.get(z.amZug)
    if (!auto) return

    const punkte = [
      { x: auto.position.x, z: auto.position.z },
      ...weg.map((f) => autoPunkt(f, 0, 1)),
    ]
    const richtungen = [
      auto.rotation.y,
      ...weg.map((f) => -Math.atan2(FELDER[f]!.richtung.z, FELDER[f]!.richtung.x)),
    ]
    fahrt = {
      objekt: auto,
      punkte,
      richtungen,
      dauer: Math.max(0.28, weg.length * 0.11),
      zeit: 0,
    }
  }

  function aktualisiere(dt: number): void {
    if (fahrt) {
      fahrt.zeit += dt
      const f = Math.min(1, fahrt.zeit / fahrt.dauer)
      const abschnitte = fahrt.punkte.length - 1
      const genau = f * abschnitte
      const i = Math.min(abschnitte - 1, Math.floor(genau))
      const t = genau - i
      const a = fahrt.punkte[i]!
      const b = fahrt.punkte[i + 1]!
      fahrt.objekt.position.x = a.x + (b.x - a.x) * t
      fahrt.objekt.position.z = a.z + (b.z - a.z) * t
      // Leichtes Wippen, damit die Fahrt Gewicht bekommt.
      fahrt.objekt.position.y = EBENE.auto + Math.sin(f * Math.PI) * 0.8
      fahrt.objekt.rotation.y = kurzesterWinkel(
        fahrt.richtungen[i]!,
        fahrt.richtungen[i + 1]!,
        t,
      )
      if (f >= 1) fahrt = null
    }

    for (let i = wuechse.length - 1; i >= 0; i--) {
      const w = wuechse[i]!
      w.zeit += dt
      const f = Math.min(1, w.zeit / WUCHSDAUER)
      // Weich anlaufen, am Ende leicht ueberschwingen.
      const e = 1 - Math.pow(1 - f, 3)
      w.objekt.scale.y = 0.04 + (1.04 - 0.04) * e - 0.04 * Math.sin(f * Math.PI)
      if (f >= 1) {
        w.objekt.scale.y = 1
        wuechse.splice(i, 1)
      }
    }
  }

  function ruht(): boolean {
    return fahrt === null && wuechse.length === 0
  }

  function markiereFeld(index: number | null): void {
    if (index === null) {
      marke.visible = false
      return
    }
    const feld = FELDER[index]
    if (!feld) {
      marke.visible = false
      return
    }
    marke.position.set(feld.mitte.x, EBENE.symbol + 0.2, feld.mitte.z)
    marke.visible = true
  }

  function zeigeAuswahl(felder: readonly number[]): void {
    const menge = new Set(felder)
    auswahlFlaechen.forEach((m, i) => {
      m.visible = menge.has(i)
    })
    treffer.visible = menge.size > 0
  }

  function feldUnter(kamera: THREE.Camera, ndcX: number, ndcY: number): number | null {
    if (!treffer.visible) return null
    strahl.setFromCamera(new THREE.Vector2(ndcX, ndcY), kamera)
    const sichtbar = treffer.children.filter(
      (m) => auswahlFlaechen[(m.userData as { feld: number }).feld]?.visible,
    )
    const t = strahl.intersectObjects(sichtbar, false)
    const erster = t[0]
    if (erster) return (erster.object.userData as { feld: number }).feld

    // Kein genauer Treffer: Strahl auf die Brettebene legen und das naechste
    // hervorgehobene Feld nehmen, wenn es nah genug liegt.
    const ebene = new THREE.Plane(new THREE.Vector3(0, 1, 0), -EBENE.symbol)
    const punkt = new THREE.Vector3()
    if (!strahl.ray.intersectPlane(ebene, punkt)) return null
    let bestes: number | null = null
    let abstand = 34
    for (const feld of FELDER) {
      if (!auswahlFlaechen[feld.index]?.visible) continue
      const d = Math.hypot(punkt.x - feld.mitte.x, punkt.z - feld.mitte.z)
      if (d < abstand) {
        abstand = d
        bestes = feld.index
      }
    }
    return bestes
  }

  return { wurzel, zeige, markiereFeld, zeigeAuswahl, feldUnter, aktualisiere, ruht }
}

/** Interpoliert zwei Winkel auf dem kuerzeren Weg - sonst dreht sich das Auto einmal um. */
function kurzesterWinkel(a: number, b: number, t: number): number {
  let d = ((b - a + Math.PI) % (Math.PI * 2)) - Math.PI
  if (d < -Math.PI) d += Math.PI * 2
  return a + d * t
}

function ringGeometrie(): THREE.BufferGeometry {
  const g = new THREE.RingGeometry(11, 14, 32)
  g.rotateX(-Math.PI / 2)
  return g
}

/** Nur fuer Belegbilder: alle Gebaeude eines Hotels bis zu einer Stufe aufbauen. */
export function baueHotelStufe(
  hotel: HotelId,
  gebaeude: number,
  freizeitpark: boolean,
): THREE.Group {
  const gruppe = new THREE.Group()
  for (let i = 0; i < Math.min(gebaeude, HOTELS[hotel].gebaeude.length); i++) {
    gruppe.add(baueGebaeude(hotel, i))
  }
  if (freizeitpark) gruppe.add(baueFreizeitpark(hotel))
  return gruppe
}
